import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ArrowRight, Scale, Download, Upload, BarChart3 } from 'lucide-react';
import logo from './images/spore-logo.png';
import { mushroomTypes, bagTypes, stage_names } from './constants';

// All batch data is served by Cloudflare Pages Functions in /functions/api,
// which talk to a D1 database. Same-origin, so a relative path is enough.
const API_URL = '/api/batches';

const MushroomTracker = () => {
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewBatch, setShowNewBatch] = useState(false);
  const [newBatch, setNewBatch] = useState({
    mushroomType: '',
    bagCount: '',
    inoculationDate: new Date().toISOString().split('T')[0],
    startStage: 'Incubation'
  });

  // Load batches from the D1-backed API on mount
  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error(`Failed to load batches (${res.status})`);

      // The API already returns batches in the app's camelCase shape.
      const data = await res.json();
      setBatches(data);
    } catch (error) {
      console.error('Error loading batches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addBatch = async () => {
    if (newBatch.mushroomType && newBatch.bagCount) {
      const batch = {
        mushroomType: newBatch.mushroomType,
        bagCount: newBatch.bagCount,
        bagType: newBatch.bagType,
        spawnId: newBatch.spawnId,
        inoculationDate: newBatch.inoculationDate,
        startStage: newBatch.startStage
      };

      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(batch)
        });

        if (!res.ok) throw new Error(`Failed to add batch (${res.status})`);

        // Reload batches from database
        await loadBatches();

        setNewBatch({
          mushroomType: '',
          bagCount: '',
          inoculationDate: new Date().toISOString().split('T')[0],
          startStage: 'Incubation'
        });
        setShowNewBatch(false);
      } catch (error) {
        console.error('Error adding batch:', error);
        alert('Failed to add batch. Please try again.');
      }
    }
  };

  const removeBatch = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    if (!batch) return;

    if(!window.confirm('This action will permanently delete this batch. Are you sure you want to proceed?')) return;

      try {
        const res = await fetch(`${API_URL}/${batchId}`, { method: 'DELETE' });

        if (!res.ok) throw new Error(`Failed to delete batch (${res.status})`);

        await loadBatches();

      } catch (error) {
        console.error('Error deleting batch:', error);
        alert('Failed to delete batch. Please try again.');
      }
};

  // If a batch's bag count has just hit zero, it's done — automatically
  // advance it to the Harvested stage regardless of what stage it was in.
  const buildAutoAdvance = (batch, newBagCount) => {
    if (newBagCount > 0 || batch.stage >= 2) return null;
    return {
      stage: 2,
      stageHistory: [...batch.stageHistory, {
        stage: 'Harvested',
        date: new Date().toISOString().split('T')[0]
      }]
    };
  };

  const removeBag = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    if (!batch || batch.bagCount <= 0) return;

    const updatedBadBags = [...(batch.badBags || []), {
      date: new Date().toISOString().split('T')[0],
      stage: stage_names[batch.stage]
    }];
    const newBagCount = parseInt(batch.bagCount) - 1;
    const autoAdvance = buildAutoAdvance(batch, newBagCount);

    try {
      const res = await fetch(`${API_URL}/${batchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bagCount: String(newBagCount),
          badBags: updatedBadBags,
          ...(autoAdvance || {})
        })
      });

      if (!res.ok) throw new Error(`Failed to remove bag (${res.status})`);

      await loadBatches();
    } catch (error) {
      console.error('Error removing bag:', error);
      alert('Failed to remove bag. Please try again.');
    }
  };

  const advanceStage = async (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    if (!batch || batch.stage >= 2) return;

    const newStage = batch.stage + 1;
    const updatedHistory = [...batch.stageHistory, {
      stage: stage_names[newStage],
      date: new Date().toISOString().split('T')[0]
    }];

    try {
      const res = await fetch(`${API_URL}/${batchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stage: newStage,
          stageHistory: updatedHistory
        })
      });

      if (!res.ok) throw new Error(`Failed to advance stage (${res.status})`);

      await loadBatches();
    } catch (error) {
      console.error('Error advancing stage:', error);
      alert('Failed to advance stage. Please try again.');
    }
  };

  const recordHarvest = async (batchId, weight, bagsHarvested) => {
    const batch = batches.find(b => b.id === batchId);
    if (!batch) return;

    const weightNum = parseFloat(weight);
    const bagsNum = parseInt(bagsHarvested, 10);

    if (!weightNum || weightNum <= 0) {
      alert('Please enter a harvest weight greater than 0.');
      return;
    }
    if (!bagsNum || bagsNum <= 0) {
      alert('Please enter the number of bags harvested.');
      return;
    }
    if (bagsNum > parseInt(batch.bagCount, 10)) {
      alert(`Only ${batch.bagCount} bag(s) remain in this batch.`);
      return;
    }

    const updatedHarvestEntries = [...(batch.harvestEntries || []), {
      date: new Date().toISOString().split('T')[0],
      weight: Math.round(weightNum * 100) / 100,
      bagsHarvested: bagsNum
    }];
    const newBagCount = parseInt(batch.bagCount, 10) - bagsNum;
    const autoAdvance = buildAutoAdvance(batch, newBagCount);

    try {
      const res = await fetch(`${API_URL}/${batchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bagCount: String(newBagCount),
          harvestEntries: updatedHarvestEntries,
          ...(autoAdvance || {})
        })
      });

      if (!res.ok) throw new Error(`Failed to record harvest (${res.status})`);

      await loadBatches();
    } catch (error) {
      console.error('Error recording harvest:', error);
      alert('Failed to record harvest. Please try again.');
    }
  };

  // Reads the weight + bags inputs sitting next to whichever element
  // triggered it (button click or Enter keypress) and submits a harvest entry.
  const submitHarvestRow = (triggerEl, batchId) => {
    const row = triggerEl.closest('.harvest-input-row');
    if (!row) return;
    const [weightInput, bagsInput] = row.querySelectorAll('input');
    if (!weightInput.value || !bagsInput.value) return;

    recordHarvest(batchId, weightInput.value, bagsInput.value);
    weightInput.value = '';
    bagsInput.value = '';
  };

  const getDaysInTotal = (batch) => {
    if (batch.stageHistory.length === 0) return 0;
    const firstStageDate = new Date(batch.stageHistory[0].date);
    const today = new Date();
    const diffTime = Math.abs(today - firstStageDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDaysInStage = (batch) => {
    if (batch.stageHistory.length === 0) return 0;
    const lastStageDate = new Date(batch.stageHistory[batch.stageHistory.length - 1].date);
    const today = new Date();
    const diffTime = Math.abs(today - lastStageDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const groupedBatches = stage_names.map((stage, idx) => ({
    stage,
    batches: batches.filter(b => b.stage === idx)
  }));

  const exportData = () => {
    const dataStr = JSON.stringify(batches, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mushroom-farm-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importData = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const importedBatches = JSON.parse(e.target.result);

          const res = await fetch(`${API_URL}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batches: importedBatches })
          });

          if (!res.ok) throw new Error(`Import failed (${res.status})`);

          await loadBatches();
          alert('Data imported successfully!');
        } catch (error) {
          console.error('Import error:', error);
          alert('Error importing data. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-6">
      {isLoading ? (
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-4xl mb-4">🍄</div>
            <p className="text-gray-600">Loading your batches...</p>
          </div>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-wrap justify-center md:justify-between items-center mb-4">
	    <div>
	      <img className="object-center md:object-left max-w-64 max-h-64" src={logo} alt="Logo" />
            </div>
            <div>
	      <h1 className="text-2xl md:text-4xl justify-self-center text-center md:text-left font-bold font-[Syncopate] text-blue-700">A better life...with mycelium!</h1>
            </div>
	  </div>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/reports"
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg transition"
              title="View yield reports"
            >
              <BarChart3 size={20} />
              Reports
            </Link>
            <button
              onClick={exportData}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg transition"
              title="Export data for backup"
            >
              <Download size={20} />
              Export
            </button>
            <label className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg transition cursor-pointer">
              <Upload size={20} />
              Import
              <input
                type="file"
                accept=".json"
                onChange={importData}
                className="hidden"
              />
            </label>
            <button
              onClick={() => setShowNewBatch(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 shadow-lg transition"
            >
              <Plus size={20} />
              New Batch
            </button>
          </div>
        </div>

        {showNewBatch && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-emerald-200">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Create New Batch</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mushroom Type</label>
                <select
                  value={newBatch.mushroomType}
                  onChange={(e) => setNewBatch({...newBatch, mushroomType: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select type...</option>
                  {Object.keys(mushroomTypes).map(type => (
                    <option key={type} value={type}>{type} ({mushroomTypes[type].incubationDays[0] + '-' + mushroomTypes[type].incubationDays[1]} days)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Bags</label>
                <input
                  type="number"
                  value={newBatch.bagCount}
                  onChange={(e) => setNewBatch({...newBatch, bagCount: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                  placeholder="e.g., 50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bag Type</label>
                <select
                  value={newBatch.bagType}
                  onChange={(e) => setNewBatch({...newBatch, bagType: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select type...</option>
                  {Object.values(bagTypes).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Spawn ID</label>
                <input
                  type="text"
                  value={newBatch.spawnId}
                  onChange={(e) => setNewBatch({...newBatch, spawnId: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                  placeholder="e.g., PACNS-01012025-123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inoculation Date</label>
                <input
                  type="date"
                  value={newBatch.inoculationDate}
                  onChange={(e) => setNewBatch({...newBatch, inoculationDate: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Starting Stage</label>
                <select
                  value={newBatch.startStage}
                  onChange={(e) => setNewBatch({...newBatch, startStage: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
                >
                  <option value="Incubation">Incubation</option>
                  <option value="Fruiting">Fruiting</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={addBatch}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg transition"
              >
                Create Batch
              </button>
              <button
                onClick={() => setShowNewBatch(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {groupedBatches.map(({ stage, batches: stageBatches }) => (
            <div key={stage} className="bg-white rounded-xl shadow-lg p-5 border-t-4 border-emerald-500">
              <h2 className="text-xl font-bold mb-4 text-gray-800">{stage}</h2>
              <div className="space-y-3">
                {stageBatches.length === 0 ? (
                  <p className="text-gray-400 text-center py-8 italic">No batches</p>
                ) : (
                  stageBatches.map(batch => {
                    const typeInfo = mushroomTypes[batch.mushroomType];
                    const daysInStage = getDaysInStage(batch);
                    const daysInTotal = getDaysInTotal(batch);
                    const totalHarvestWeight = (batch.harvestEntries || [])
                      .reduce((sum, h) => sum + h.weight, 0);
                    
                    return (
                      <div
                        key={batch.id}
                        className={`${typeInfo.color} border-2 rounded-lg p-4 space-y-2`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-gray-800">{batch.mushroomType}{!batch.stages.includes(0) && ' Blocks'}</h3>
                            <p className="text-sm text-white">{batch.bagCount} bags / {batch.bagType}</p>
                            <p className="text-xs text-white mt-1">Started: {batch.inoculationDate}</p>
			    <p className="text-xs text-white mt-1">Spawn ID: {batch.spawnId}</p>
                          </div>
                          <div className="text-xs text-center font-bold min-w-max bg-white px-2 py-1 rounded"> 
			  <span>
			    Total / Stage
			  </span>
			  <br></br>
			  <span>
			    {daysInTotal} days / {daysInStage} days
                          </span>
			  </div>
                        </div>

                        {batch.stage === 0 && (
                          <p className="text-xs text-gray-600">
                            Expected: {typeInfo.incubationDays[0] + '-' + typeInfo.incubationDays[1]} days
                          </p>
                        )}

                        {(batch.badBags || []).length > 0 && (
                          <div className="bg-white rounded px-2 py-2 text-xs mt-2">
                            <p className="font-semibold text-red-600 mb-1">Bad Bags Removed:</p>
                            {(batch.badBags || []).map((bad, idx) => (
                              <div key={idx} className="text-gray-600">
                                • {bad.date} ({bad.stage})
                              </div>
                            ))}
                          </div>
                        )}
			
			<button
			    onClick={() => removeBatch(batch.id)}
			    className="w-full bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition mt-2"
			>
			   Delete Batch
			</button>
                        {(batch.stage === 0 || batch.stage === 1) && (
                          <button
                            onClick={() => removeBag(batch.id)}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm transition mt-2"
                          >
                            Remove Bad Bag
                          </button>
                        )}

                        {batch.stage === 1 && (
                          <div className="bg-white rounded px-2 py-2 mt-2 space-y-2">
                            {(batch.harvestEntries || []).length > 0 && (
                              <div className="text-xs">
                                <p className="font-semibold text-emerald-700 mb-1">Harvest Entries:</p>
                                {batch.harvestEntries.map((h, idx) => (
                                  <div key={idx} className="text-gray-600">
                                    • {h.date}: {h.weight.toFixed(2)} lbs ({h.bagsHarvested} bag{h.bagsHarvested === 1 ? '' : 's'})
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2 harvest-input-row">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Weight (lbs)"
                                className="flex-1 min-w-0 border-2 border-gray-300 rounded px-2 py-1 text-sm"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') submitHarvestRow(e.target, batch.id);
                                }}
                              />
                              <input
                                type="number"
                                min="1"
                                step="1"
                                max={batch.bagCount}
                                placeholder="Bags"
                                className="w-20 border-2 border-gray-300 rounded px-2 py-1 text-sm"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') submitHarvestRow(e.target, batch.id);
                                }}
                              />
                              <button
                                onClick={(e) => submitHarvestRow(e.target, batch.id)}
                                className="bg-emerald-600 text-white px-3 py-1 rounded text-sm hover:bg-emerald-700"
                              >
                                <Scale size={16} />
                              </button>
                            </div>
                          </div>
                        )}

                        {batch.stage === 2 && (batch.harvestEntries || []).length > 0 && (
                          <div className="bg-white rounded px-2 py-2 text-sm mt-2">
                            <p className="font-semibold text-emerald-700 mb-1">
                              ✓ {totalHarvestWeight.toFixed(2)} lbs harvested total
                            </p>
                            <div className="text-xs text-gray-600 space-y-0.5">
                              {batch.harvestEntries.map((h, idx) => (
                                <div key={idx}>
                                  • {h.date}: {h.weight.toFixed(2)} lbs ({h.bagsHarvested} bag{h.bagsHarvested === 1 ? '' : 's'})
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {batch.stage === 0 && (
                          <button
                            onClick={() => advanceStage(batch.id)}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded flex items-center justify-center gap-2 text-sm transition mt-2"
                          >
                            Move to {stage_names[1]} <ArrowRight size={16} />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>

        {batches.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Batches</p>
                <p className="text-2xl font-bold text-emerald-700">{batches.length}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Bags</p>
                <p className="text-2xl font-bold text-blue-700">
                  {batches.reduce((sum, b) => sum + parseInt(b.bagCount || 0), 0)}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">In Production</p>
                <p className="text-2xl font-bold text-purple-700">
                  {batches.filter(b => b.stage < 2).length}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Harvested</p>
                <p className="text-2xl font-bold text-amber-700">
                  {batches.filter(b => b.stage === 2).length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default MushroomTracker;
